// CalendarRescheduleListener — глобальный слушатель `study-plan:calendar.ready`.
//
// Монтируется один раз в __root.tsx (реагирует на ВСЕ открытые поверхности:
// календарь + деталь плана MiniCalendar + дашборд — rule §14 «realtime во все
// поверхности», не только detail-роут).
//
// При reschedule с фактическим сдвигом сервер эмитит calendar.ready с reason.
//   - toast «Расписание обновлено» при reason ∈ {examDate-changed,
//     schedule-changed, nightly-rolling} (заметные для пользователя сдвиги; для
//     generated/progress-advanced/manual-reset toast не нужен — это фон).
//   - markRescheduled(planId) → подсветка перемещённых уроков (PlanMonthGrid/
//     agenda читают флаг из стора).
//
// Сами данные до-синкает PowerSync (slots реактивны) — socket = сигнал для
// toast/подсветки. TanStack Query кэш плана (api.studyPlan.get) инвалидируем,
// т.к. деталь плана читает прогресс/секции через tRPC.

import { toast } from "@studyhub/ui/toast";
import { useTranslation } from "react-i18next";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { api } from "@/lib/trpc-client";
import { useCalendarRescheduleStore } from "@/stores/calendar-reschedule";

/** reason'ы, заметные пользователю (показываем toast «Расписание обновлено»). */
const TOAST_REASONS: ReadonlySet<string> = new Set([
	"examDate-changed",
	"schedule-changed",
	"nightly-rolling",
]);

export function CalendarRescheduleListener(): null {
	const { t } = useTranslation("study-plan");
	const utils = api.useUtils();
	const markRescheduled = useCalendarRescheduleStore((s) => s.markRescheduled);

	useSocketEvent("study-plan:calendar.ready", (payload) => {
		// PLANNING-REWORK: пересчёт per-USER → planId опционален (нет одного плана).
		// Список планов перечитываем всегда.
		void utils.studyPlan.list.invalidate();
		if (payload.planId) {
			// Конкретный план: подсветка его уроков + инвалидация detail-кэша.
			markRescheduled(payload.planId);
			void utils.studyPlan.get.invalidate({ id: payload.planId } as never);
		} else {
			// Per-user пересчёт без planId: инвалидируем detail-кэш ВСЕХ планов
			// (invalidate без input = все studyPlan.get) — открытая деталь любого
			// плана перечитает прогресс/секции. Подсветку конкретного плана не
			// делаем (planId неизвестен); slots до-синкает PowerSync, toast уведомит.
			void utils.studyPlan.get.invalidate();
		}
		if (TOAST_REASONS.has(payload.reason)) {
			toast(t("calendar.rescheduled"));
		}
	});

	return null;
}
